import { t } from "@/global";
import { setAbPeersTag } from "@/services/api";
import { API } from "@/services/typings";
import { ModalForm, ProFormItem } from "@ant-design/pro-form";
import { ActionType } from "@ant-design/pro-table";
import { Radio, message, Flex } from "antd";
import { useEffect, useState } from "react";
import { buildTag } from "./TagsCard";

type SetTagProps = {
  visible: boolean;
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  actionRef: React.MutableRefObject<ActionType | undefined>;
  profile: API.SharedAbProfile;
  abPeers: API.AbPeer[];
  abTags: API.AbTag[];
}

type TagOperation = 'set' | 'add' | 'remove' | 'clear';

const SetTagModal: React.FC<SetTagProps> = (props) => {
  const [operation, setOperation] = useState<TagOperation>('set');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const showTagSelector = operation !== 'clear';
  const requireTagSelection = operation !== 'clear';

  useEffect(() => {
    if (props.visible) {
      setOperation('set');
      setSelectedTags([]);
    }
  }, [props.visible]);

  const handleUpdate = async () => {
    const hide = message.loading('Updating');
    try {
      let tags: string[];
      let op: string;

      if (operation === 'clear') {
        op = 'set';
        tags = [];
      } else if (operation === 'set') {
        op = 'set';
        tags = selectedTags;
      } else if (operation === 'add') {
        op = 'add';
        tags = selectedTags;
      } else {
        op = 'remove';
        tags = selectedTags;
      }

      const payload = {
        tags: tags,
        ab: props.profile.guid,
        guids: props.abPeers.map((x) => x.guid),
        op: op,
      };
      await setAbPeersTag({ data: payload });
      hide();
      message.success('Updated successfully');
      return true;
    } catch (error) {
      hide();
      message.error(
        typeof error == 'string' ? (error as string) : 'Update failed, please try again!',
      );
      return false;
    }
  };

  const buildTagWrapper = (abTag: API.AbTag) => {
    const selected = selectedTags.includes(abTag.name);
    return buildTag(abTag, selected, () => {
      if (selected) {
        setSelectedTags(selectedTags.filter(tag => tag !== abTag.name));
      } else {
        setSelectedTags([...selectedTags, abTag.name]);
      }
    }, undefined, undefined);
  };



  return (
    <ModalForm
      title={t('set_tags', true)}
      width="400px"
      open={props.visible}
      onOpenChange={props.setModalVisible}
      onFinish={async () => {
        if (requireTagSelection && selectedTags.length === 0) {
          message.error('Please select at least one tag');
          return;
        }
        const success = await handleUpdate();
        if (success) {
          if (props.actionRef.current) {
            props.actionRef.current?.reloadAndRest?.();
          }
          props.setModalVisible(false);
        }
      }}
      preserve={false}
      modalProps={{
        maskClosable: false,
        destroyOnClose: true,
        onCancel: () => {
          props.setModalVisible(false);
        },
      }}
    >
      <ProFormItem label={t('Operation')}>
        <Radio.Group value={operation} onChange={(e) => setOperation(e.target.value)}>
          <Radio value="set">{t('set_to_selected_tags')}</Radio>
          <Radio value="add">{t('add_selected_tags')}</Radio>
          <Radio value="remove">{t('remove_selected_tags')}</Radio>
          <Radio value="clear">{t('clear_all_tags')}</Radio>
        </Radio.Group>
      </ProFormItem>

      {showTagSelector && props.abTags.length > 0 && <ProFormItem label={t('Tags')}>
        <Flex wrap="wrap" gap="small">
          {props.abTags.map((tag) => (
            buildTagWrapper(tag)
          ))}
        </Flex>
      </ProFormItem>}
      {showTagSelector && props.abTags.length === 0 && <ProFormItem>
        {t('no_tags_available', true)}
      </ProFormItem>}
    </ModalForm>
  )
}

export default SetTagModal;

